/**
 * SubPageTabs registry — Phase 6.5 nav IA remediation.
 *
 * Maps each NAV_V2 parent route that has sub-pages to its
 * in-page tab definitions. The dashboard shell (or a section
 * layout) reads this registry and renders `<SubPageTabs>` at the
 * top of any page whose pathname matches a registered parent.
 *
 * Design (Paul ratified 2026-05-27): the Nav v2 sidebar stays
 * 2-levels-deep. Deeper navigation goes IN-PAGE via these tabs.
 * Matches the macOS / Linear / Notion pattern.
 *
 * Update rules:
 * - First tab is usually "Overview" pointing at the parent route
 *   itself (when the parent has its own page.tsx). When the parent
 *   is a router rewrite (e.g. `/hr` rewrites to `/hr/onboarding`),
 *   skip the Overview tab and start with the canonical child.
 * - Subsequent tabs in display order (left → right), grouped
 *   logically — not always alphabetical.
 * - Dynamic-segment children (`[id]`, `[facilityId]`) are NOT tabs.
 *   They're detail pages reached from list-row clicks.
 * - Settings is intentionally OMITTED — it has its own 2-pane shell
 *   (Phase 5 `settings-shell.tsx`), except the approved People Settings family.
 * - Large sections may GROUP tabs: the tab's `href` is the group's
 *   landing page and `activeFor` lists the other route prefixes that
 *   belong to the group, so deep links still highlight their tab
 *   (longest-prefix winner across href + activeFor). The roster
 *   section uses the approved five-group task shape; in-group
 *   navigation is a second row rendered per-page by `RosterSectionNav`.
 * - A tab href may exceptionally point OUTSIDE its parent section when
 *   two sections share one canonical surface (e.g. `/payroll` →
 *   `/roster/timesheets`); the old in-section route must redirect.
 *
 * Sub-page rendering is automatic: the dashboard surface reads
 * `resolveSubPageTabs(currentPath)` and renders tabs when the
 * result is non-null. No per-page wiring required.
 */

import type { SubPageTab } from '@statura/ui';
import { PAYROLL_NAV_SECTION_TABS } from '@/lib/payroll/payroll-nav-sections';
import { ROSTER_PRIMARY_TABS } from '@/lib/roster/roster-navigation-ia';

/**
 * Parent route → tab definitions. The key is the parent's canonical
 * route (matches one of the `NAV_V2_ITEM.href` values OR a known
 * top-level section like `/hr`).
 */
/**
 * Brokered Support at Home invoicing, in the order the work happens: resolve
 * anything held for a pay/bill decision, then raise the invoice. Shared by all
 * three routes so the tab strip survives navigation between them.
 */
const FINANCE_INVOICING_TABS: ReadonlyArray<SubPageTab> = [
  { id: 'overview', label: 'Overview', href: '/finance/invoices' },
  { id: 'billing-review', label: 'Billing review', href: '/finance/billing-review' },
  { id: 'brokerage-invoices', label: 'Brokerage invoices', href: '/finance/brokerage-invoices' },
];

const SUB_PAGE_TABS_BY_PARENT: Record<string, ReadonlyArray<SubPageTab>> = {
  // Roster navigation is generated from one task-shaped authority shared with
  // the second row. Data-health routes remain stable deep links but resolve
  // contextually into Patterns & periods or Time & closeout rather than occupying a sixth tab.
  '/roster': ROSTER_PRIMARY_TABS,
  '/people': [
    { id: 'home', label: 'Home', href: '/people', activeFor: ['/people/workers'] },
    {
      id: 'onboarding',
      label: 'Join & leave',
      href: '/people/onboarding',
      activeFor: ['/people/offboarding'],
    },
    {
      id: 'compliance',
      label: 'Screening & credentials',
      href: '/people/compliance',
      activeFor: ['/people/compliance/screening', '/people/compliance/certifications'],
    },
    {
      id: 'learning',
      label: 'Training & skills',
      href: '/people/learning',
      activeFor: [
        '/people/learning/courses',
        '/people/learning/analytics',
        '/people/learning/external-evidence',
        '/people/learning/competencies',
      ],
    },
    {
      id: 'policies',
      label: 'Policies & conduct',
      href: '/people/policies-conduct',
      activeFor: ['/people/conduct-decisions'],
    },
    { id: 'development', label: 'Reviews & supervision', href: '/people/development' },
    { id: 'evidence', label: 'Evidence & reports', href: '/people/evidence' },
  ],
  // Payroll — four question-shaped groups (programme F1). Flat 10-tab
  // strip hid award-review / handoff / rules behind overflow. Timesheets
  // stay on /roster/timesheets (NAV-01); Pay period claims that surface
  // via the readiness landing, not a fifth tab. Pay breakdown lives under
  // Pay run (`/payroll/timesheet/[id]/breakdown`). Pay rules is a
  // documented cross-section hop into settings.
  '/payroll': PAYROLL_NAV_SECTION_TABS,
  '/home-care': [
    {
      id: 'service-delivery',
      label: 'Service delivery',
      href: '/home-care/service-delivery',
      activeFor: [
        '/home-care/schedules',
        '/home-care/exceptions',
        '/home-care/care-delivery-settings',
      ],
    },
    {
      id: 'care-delivery-settings',
      label: 'Delivery policies',
      href: '/home-care/care-delivery-settings',
    },
    {
      id: 'readiness',
      label: 'Readiness',
      href: '/home-care/readiness',
      activeFor: [
        '/home-care/assessments',
        '/home-care/classifications',
        '/home-care/service-areas',
        '/home-care/at-hm',
      ],
    },
    {
      id: 'budgets',
      label: 'Budgets',
      href: '/home-care/budgets',
      activeFor: ['/home-care/budget-explainer'],
    },
    { id: 'notes-review', label: 'Note review', href: '/home-care/notes-review' },
    { id: 'funding-changes', label: 'Funding changes', href: '/home-care/funding-changes' },
    { id: 'claims', label: 'Claims', href: '/home-care/claims' },
    { id: 'statements', label: 'Statements', href: '/home-care/statements' },
    { id: 'reconciliation', label: 'Reconciliation', href: '/home-care/reconciliation' },
  ],
  '/compliance/standards': [
    { id: 'overview', label: 'Standards', href: '/compliance/standards' },
    { id: 'engagement', label: 'Engagement', href: '/compliance/standards?tab=engagement' },
    {
      id: 'tagged-evidence',
      label: 'Tagged evidence',
      href: '/compliance/standards?tab=tagged-evidence',
    },
  ],
  '/compliance/governance': [
    { id: 'overview', label: 'Overview', href: '/compliance/governance' },
    { id: 'risk-register', label: 'Risk register', href: '/compliance/governance/risk-register' },
    { id: 'conflicts', label: 'Conflicts', href: '/compliance/governance/conflicts' },
    { id: 'skills-matrix', label: 'Skills matrix', href: '/compliance/governance/skills-matrix' },
    { id: 'qcab', label: 'QCAB', href: '/compliance/governance/qcab' },
  ],
  '/compliance/whs': [
    { id: 'overview', label: 'Overview', href: '/compliance/whs' },
    { id: 'incidents', label: 'Incidents', href: '/compliance/whs/incidents' },
    { id: 'hazards', label: 'Hazards', href: '/compliance/whs/hazards' },
    { id: 'chemicals', label: 'Chemicals', href: '/compliance/whs/chemicals' },
    { id: 'equipment', label: 'Equipment', href: '/compliance/whs/equipment' },
    { id: 'drills', label: 'Drills', href: '/compliance/whs/drills' },
    { id: 'psychosocial', label: 'Psychosocial', href: '/compliance/whs/psychosocial' },
  ],
  '/people/policies-conduct/code': [
    { id: 'overview', label: 'Overview', href: '/people/policies-conduct/code' },
    {
      id: 'acknowledgments',
      label: 'Legacy history',
      href: '/people/policies-conduct/code/acknowledgments',
    },
    { id: 'allegations', label: 'Allegations', href: '/people/policies-conduct/code/allegations' },
    {
      id: 'investigations',
      label: 'Investigations',
      href: '/people/policies-conduct/code/investigations',
    },
    { id: 'reports', label: 'Reports', href: '/people/policies-conduct/code/reports' },
    { id: 'analytics', label: 'Analytics', href: '/people/policies-conduct/code/analytics' },
  ],
  '/compliance/whistleblower': [
    { id: 'overview', label: 'Overview', href: '/compliance/whistleblower' },
    { id: 'policy', label: 'Policy', href: '/compliance/whistleblower?tab=policy' },
    { id: 'recipients', label: 'Recipients', href: '/compliance/whistleblower?tab=recipients' },
    { id: 'protection', label: 'Protection', href: '/compliance/whistleblower?tab=protection' },
    {
      id: 'investigations',
      label: 'Investigations',
      href: '/compliance/whistleblower?tab=investigations',
    },
    { id: 'remediation', label: 'Remediation', href: '/compliance/whistleblower?tab=remediation' },
    { id: 'reports', label: 'Reports', href: '/compliance/whistleblower?tab=reports' },
  ],
  '/compliance/prudential': [
    { id: 'overview', label: 'Overview', href: '/compliance/prudential' },
    { id: 'deposits', label: 'Deposits', href: '/compliance/prudential/deposits' },
    { id: 'liquidity', label: 'Liquidity', href: '/compliance/prudential/liquidity' },
    {
      id: 'permitted-uses',
      label: 'Permitted Uses',
      href: '/compliance/prudential/permitted-uses',
    },
    { id: 'pricing', label: 'Pricing', href: '/compliance/prudential/pricing' },
    { id: 'governance', label: 'Governance', href: '/compliance/prudential/governance' },
    { id: 'reports', label: 'Reports', href: '/compliance/prudential/reports' },
  ],
  '/compliance/ipc': [
    { id: 'overview', label: 'Overview', href: '/compliance/ipc' },
    { id: 'outbreaks', label: 'Outbreaks', href: '/compliance/ipc/outbreaks' },
    { id: 'surveillance', label: 'Surveillance', href: '/compliance/ipc/surveillance' },
    { id: 'reports', label: 'Reports', href: '/compliance/ipc/reports' },
  ],
  '/compliance/complaints': [
    { id: 'overview', label: 'Overview', href: '/compliance/complaints' },
    {
      id: 'communications',
      label: 'Communications',
      href: '/compliance/complaints?tab=communications',
    },
    { id: 'escalations', label: 'Escalations', href: '/compliance/complaints?tab=escalations' },
    { id: 'resolution', label: 'Resolution', href: '/compliance/complaints?tab=resolution' },
    {
      id: 'improvements',
      label: 'Improvements',
      href: '/compliance/continuous-improvement?source=COMPLAINT',
    },
    { id: 'analytics', label: 'Analytics', href: '/compliance/complaints?tab=analytics' },
    { id: 'feedback', label: 'Feedback', href: '/compliance/complaints?tab=feedback' },
  ],
  '/compliance/restrictive-practices': [
    { id: 'overview', label: 'Overview', href: '/compliance/restrictive-practices' },
    { id: 'bsp', label: 'Behaviour Support Plans', href: '/compliance/restrictive-practices/bsp' },
  ],
  '/compliance/continuous-improvement': [
    { id: 'overview', label: 'Plan', href: '/compliance/continuous-improvement' },
    { id: 'actions', label: 'Actions', href: '/compliance/continuous-improvement?view=actions' },
    { id: 'review', label: 'Review', href: '/compliance/continuous-improvement?view=review' },
  ],
  '/compliance/star-ratings': [
    { id: 'overview', label: 'Overview', href: '/compliance/star-ratings' },
    { id: 'methodology', label: 'Methodology', href: '/compliance/star-ratings/methodology' },
  ],
  '/dashboards': [
    { id: 'huddle', label: 'Huddle', href: '/dashboards/huddle' },
    { id: 'don-clinical', label: 'DON Clinical', href: '/dashboards/don-clinical' },
    { id: 'caseload', label: 'Caseload', href: '/dashboards/caseload' },
    { id: 'executive', label: 'Executive', href: '/dashboards/executive' },
    { id: 'deadlines', label: 'Deadlines', href: '/compliance/calendar?view=agenda' },
    { id: 'compliance', label: 'Compliance', href: '/dashboards/compliance' },
    { id: 'multi-facility', label: 'Multi-Facility', href: '/dashboards/multi-facility' },
    { id: 'operations', label: 'Operations', href: '/dashboards/operations' },
    { id: 'finance', label: 'Finance', href: '/dashboards/finance' },
    { id: 'quality', label: 'Quality', href: '/dashboards/quality' },
    { id: 'risk-correlation', label: 'Risk Correlation', href: '/dashboards/risk-correlation' },
    { id: 'workforce', label: 'Workforce', href: '/dashboards/workforce' },
    { id: 'iot', label: 'IoT', href: '/dashboards/iot' },
    { id: 'home-care-ops', label: 'Home Care Ops', href: '/dashboards/home-care-ops' },
    { id: 'facility-ops', label: 'Facility Ops', href: '/dashboards/facility-ops' },
  ],
  '/analytics': [
    { id: 'overview', label: 'Overview', href: '/analytics' },
    { id: 'reports', label: 'Reports', href: '/reports' },
    { id: 'facilities', label: 'Facilities', href: '/analytics/facilities' },
    { id: 'board-pack', label: 'Board pack', href: '/analytics/board-pack' },
    { id: 'benchmarking', label: 'Benchmarking', href: '/analytics/benchmarking' },
  ],
  '/reports': [
    { id: 'overview', label: 'Overview', href: '/reports' },
    { id: 'subscriptions', label: 'Subscriptions', href: '/reports/subscriptions' },
    // `/reports/generations` has only a dynamic-id detail page → no
    // tab. `/reports/sah` + `/reports/workforce` have no parent
    // page → rewrite tabs to their canonical first child.
    { id: 'sah-budgets', label: 'SAH Budgets', href: '/reports/sah/budgets' },
    {
      id: 'sah-classifications',
      label: 'SAH Classifications',
      href: '/reports/sah/classifications',
    },
    { id: 'workforce-recalls', label: 'Workforce Recalls', href: '/reports/workforce/recalls' },
  ],
  // Brokered Support at Home invoicing. Both `/finance/brokerage-invoices` and
  // `/finance/billing-review` shipped with no sidebar entry, no tab and no
  // orphan-allowlist entry, so the surface a brokered provider actually
  // invoices from could only be reached by typing its URL. They belong under
  // Invoices rather than as new sidebar leaves: the Finance domain is already
  // at the ≤10 L1 leaf ceiling, and adding them there fails the IA ceiling
  // tests. Billing review comes before the invoice run in the workflow — a
  // visit held for a pay/bill decision is excluded from invoicing until it is
  // resolved — so it reads left to right in the order the work happens.
  //
  // `resolveSubPageTabs` matches a parent by prefix only, so the same set is
  // registered under all three routes. Registering it under `/finance/invoices`
  // alone would render the tabs on Invoices and then drop them the moment the
  // operator clicked through to Brokerage invoices, leaving no way back.
  '/finance/invoices': FINANCE_INVOICING_TABS,
  '/finance/billing-review': FINANCE_INVOICING_TABS,
  '/finance/brokerage-invoices': FINANCE_INVOICING_TABS,
  // Overnight provider-managed billing build (2026-08-18): the unified
  // `/finance/billing` run surface plus its payer/charge/statement satellite
  // routes. Same ≤10 L1 leaf-ceiling constraint documented above applies —
  // Finance is already at 10 visible leaves for MIXED-provider FINANCE/EXEC
  // personas (nav-optimisation-phase1.test.ts), so these are in-page tabs off
  // Finance Home rather than new sidebar entries. Registered under the
  // shortest prefix (`/finance`) so `resolveSubPageTabs`'s longest-prefix rule
  // still gives `/finance/invoices`, `/finance/billing-review` and
  // `/finance/brokerage-invoices` their own more specific
  // `FINANCE_INVOICING_TABS` strip above, while every other `/finance/*`
  // route (including bare `/finance`) falls through to this one.
  '/finance': [
    { id: 'overview', label: 'Overview', href: '/finance' },
    {
      id: 'billing',
      label: 'Billing',
      href: '/finance/billing',
      // Stage 2 (Review delivery) of the unified run embeds the legacy
      // billing-review workbench — deep-linking there should still show the
      // Billing tab as active.
      activeFor: ['/finance/billing-review'],
    },
    { id: 'invoices', label: 'Invoices', href: '/finance/invoices' },
    { id: 'contributions', label: 'Contributions', href: '/finance/contributions' },
    { id: 'payers', label: 'Payers', href: '/finance/payers' },
    { id: 'charges', label: 'Charges', href: '/finance/charges' },
    { id: 'brokerage', label: 'Brokerage', href: '/finance/brokerage-invoices' },
    { id: 'statements', label: 'Statements', href: '/finance/statements' },
    { id: 'reports', label: 'Reports', href: '/finance/reports' },
  ],
  // `/finance/budgets` intentionally absent — no parent page.tsx,
  // only a single `carry-over` child. Single-tab section is pointless;
  // sidebar entry rewrites `/finance/budgets` → `/finance/budgets/carry-over`.
  '/finance/reports': [
    { id: 'overview', label: 'Overview', href: '/finance/reports' },
    { id: 'statements', label: 'Statements', href: '/finance/reports/statements' },
    {
      id: 'statement-dispatch',
      label: 'Statement Dispatch',
      href: '/finance/reports/statements/dispatch',
    },
    { id: 'aged-debtors', label: 'Aged Debtors', href: '/finance/reports/aged-debtors' },
    { id: 'reconciliation', label: 'Reconciliation', href: '/finance/reports/reconciliation' },
    {
      id: 'ar-ageing-parity',
      label: 'AR Ageing Parity',
      href: '/finance/reports/ar-ageing-parity',
    },
    {
      id: 'xero-ar-reconciliation',
      label: 'Xero AR',
      href: '/finance/reports/xero-ar-reconciliation',
    },
  ],
  '/pricing': [
    // No `/pricing/page.tsx` — Overview dropped (Codex pass 1 P2).
    { id: 'rate-cards', label: 'Rate Cards', href: '/pricing/rate-cards' },
    { id: 'agreements', label: 'Agreements', href: '/pricing/agreements' },
  ],
  // `/workflows` intentionally absent — no parent page.tsx, single
  // child (`instances`). Sidebar entry rewrites to `/workflows/instances`.
};

/**
 * The set of registered parent prefixes — used by `resolveSubPageTabs`
 * to do a longest-prefix lookup. Computed once at module load.
 */
const PARENT_PREFIXES = Object.keys(SUB_PAGE_TABS_BY_PARENT).sort(
  (a, b) => b.length - a.length, // longest first
);

/**
 * Resolve which SubPageTabs to render for the given pathname.
 * Returns null when no parent registers tabs (most pages).
 *
 * Longest-prefix winner: `/compliance/standards` resolves to the
 * `/compliance/quality` registry entry, not `/compliance`.
 */
export function resolveSubPageTabs(pathname: string): {
  parentHref: string;
  tabs: ReadonlyArray<SubPageTab>;
} | null {
  // Complaint records own their case navigation; the breadcrumb returns to the register.
  if (pathname.startsWith('/compliance/complaints/')) return null;
  if (pathname === '/contractors' || pathname.startsWith('/contractors/')) {
    return { parentHref: '/people', tabs: SUB_PAGE_TABS_BY_PARENT['/people']! };
  }
  // Shared canonical screens keep their addresses and permissions while joining
  // the roster task structure, including their contextual child routes.
  if (
    ['/workforce/availability', '/home-care/schedules'].some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    )
  )
    return { parentHref: '/roster', tabs: ROSTER_PRIMARY_TABS };
  for (const parent of PARENT_PREFIXES) {
    if (pathname === parent || pathname.startsWith(`${parent}/`)) {
      const tabs = SUB_PAGE_TABS_BY_PARENT[parent];
      if (tabs && tabs.length > 0) {
        return { parentHref: parent, tabs };
      }
    }
  }
  return null;
}

/**
 * Test/debug accessor for the raw registry. Avoid in production code
 * — use `resolveSubPageTabs` instead so the longest-prefix rule
 * applies.
 */
export function getSubPageTabsRegistry(): Readonly<Record<string, ReadonlyArray<SubPageTab>>> {
  return SUB_PAGE_TABS_BY_PARENT;
}

export function flattenSubPageTabs(): ReadonlyArray<SubPageTab & { parentHref: string }> {
  return Object.entries(SUB_PAGE_TABS_BY_PARENT).flatMap(([parentHref, tabs]) =>
    tabs.map((tab) => ({ ...tab, parentHref })),
  );
}

/**
 * path-to-go S23 — all hrefs (tab targets + activeFor prefixes + parent keys)
 * that count as nav-reachable roots for the orphan-route gate.
 */
export function listSubPageTabHrefs(): readonly string[] {
  const out = new Set<string>();
  for (const [parentHref, tabs] of Object.entries(SUB_PAGE_TABS_BY_PARENT)) {
    out.add(parentHref);
    for (const tab of tabs) {
      out.add(tab.href);
      for (const active of tab.activeFor ?? []) {
        out.add(active);
      }
    }
  }
  return [...out];
}

/**
 * Contractor record workspace tabs (contractor management W1). Dynamic `[id]`
 * records are not shell-registered parents, so the record pages render this
 * list under their identity header. Later workstreams append their tabs here
 * (Engagements W2, Visits W3, Statements W4) in display order.
 */
export function contractorRecordTabs(contractorId: string): ReadonlyArray<SubPageTab> {
  const base = `/contractors/${contractorId}`;
  return [
    { id: 'overview', label: 'Overview', href: base },
    { id: 'people', label: 'People', href: `${base}/people` },
    { id: 'compliance', label: 'Compliance', href: `${base}/compliance` },
    { id: 'bank-account', label: 'Bank account', href: `${base}/bank-account` },
  ];
}
